import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { Input, Select } from 'components/shared/Form';
import { MainContainer } from 'components/shared';
import { useValidation, useTranslation } from 'hooks';
import { translations } from './translations';
import { GroupedPermission } from 'interfaces/group';
import { ListModulesActions, ListModulesState } from 'store/ducks/modules';
import { ConfigChecklist } from 'components/settings/Roles';

import * as S from './styles';
import {
  FetchRoleActions,
  FetchRoleState,
  UpdateRoleActions,
  UpdateRoleState,
} from 'store/ducks/roles';
import {
  ListDatatableColumnConfigsActions,
  ListDatatableColumnConfigsState
} from 'store/ducks/settings/configs/list-datatable-column-configs';
import {
  ListDatatableFilterConfigsState,
  ListDatatableFilterConfigsActions
} from 'store/ducks/settings/configs';
import {
  ListReportColumnConfigsState,
  ListReportColumnConfigsActions
} from 'store/ducks/settings/configs';
import { useBuildGroupedPermission } from './hooks/useBuildGroupedPermission';

interface IParams {
  id: string;
}

export const EditUserRole: React.FC = () => {
  const { id } = useParams<IParams>();
  const formRef = useRef<FormHandles>(null);
  const { handleFormErrors } = useValidation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { getTranslation } = useTranslation(translations);
  const [selectedModule, setSelectedModule] = useState<string>();
  const [datatableColumnConfigs, setDatatableColumnConfigs] = useState<GroupedPermission>({});
  const [datatableFilterConfigs, setDatatableFilterConfigs] = useState<GroupedPermission>({});
  const [reportColumnConfigs, setReportColumnConfigs] = useState<GroupedPermission>({});
  const [defaultDatatableColumnConfig, setDefaultDatatableColumnConfig] = useState([]);
  const [defaultDatatableFilterConfigs, setDefaultDatatableFilterConfigs] = useState([]);
  const [defaultReportColumnConfigs, setDefaultReportColumnConfigs] = useState([]);
  const { buildGroupedPermission } = useBuildGroupedPermission();

  const { loading: loadingModules, data: dataModules } = useSelector<
    RootStateOrAny,
    ListModulesState
  >((state) => state.listModules);

  const { data: dataDatatableColumnConfigs } = useSelector<
    RootStateOrAny,
    ListDatatableColumnConfigsState
  >((state) => state.listDatatableColumnConfigs);

  const { data: dataDatatableFilterConfigs } = useSelector<
    RootStateOrAny,
    ListDatatableFilterConfigsState
  >((state) => state.listDatatableFilterConfigs);

  const { data: dataReportColumnConfigs } = useSelector<
    RootStateOrAny,
    ListReportColumnConfigsState
  >((state) => state.listReportColumnConfigs);

  const { loading } = useSelector<
    RootStateOrAny,
    FetchRoleState
  >((state) => state.fetchRole);

  const { loading: loadingUpdateRole } = useSelector<RootStateOrAny>(
    (state) => state.updateRole
  ) as UpdateRoleState;

  const onSuccess = useCallback(() => {
    history.push('/settings/user-roles');
  }, [history]);

  const onFetchSuccess = useCallback((data) => {
    const datatableColumnConfigs = data.datatable_column_configs.map((item: any) => ({
      id: `column-${item.config.id}`,
      label: item.config.name,
      value: `${item.config.id}`,
    }));

    setDefaultDatatableColumnConfig(() => datatableColumnConfigs);

    const datatableFilterConfigs = data.datatable_filter_configs.map((item: any) => ({
      id: `filter-${item.config.id}`,
      label: item.config.name,
      value: `${item.config.id}`,
    }));

    setDefaultDatatableFilterConfigs(() => datatableFilterConfigs);

    const reportColumnConfigs = data.report_column_configs.map((item: any) => ({
      id: `report-${item.config.id}`,
      label: item.config.name,
      value: `${item.config.id}`,
    }));

    setDefaultReportColumnConfigs(() => reportColumnConfigs);

    if (formRef.current) {
      formRef.current.setData(data);
    }
  }, []);

  const handleSubmit = useCallback<SubmitHandler>(
    async (data) => {
      try {
        const { name } = data;
        delete data.general_module_id;
        delete data.name;

        const datatableColumnConfigIds = Object.values(data.datatableConfigs ?? {})
          .flat()
          .map((value) => value);

        const datatableFilterConfigIds = Object.values(data.datatableFilters ?? {})
          .flat()
          .map((value) => value);

        const reportColumnConfigIds = Object.values(data.reportConfigs ?? {})
          .flat()
          .map((value) => value);

        formRef.current?.setErrors({});

        const newData = {
          name,
          general_module_id: selectedModule,
          datatableColumnConfigIds,
          datatableFilterConfigIds,
          reportColumnConfigIds,
        };

        const schema = Yup.object().shape({
          name: Yup.string().required('Obrigatório'),
        });

        await schema.validate(newData, {
          abortEarly: false,
        });

        dispatch(UpdateRoleActions.request(id, newData, onSuccess));
      } catch (error) {
        handleFormErrors(error, formRef);
      }
    },
    [dispatch, handleFormErrors, onSuccess, id]
  );

  const fetchData = useCallback(() => {
    if (id) {
      dispatch(FetchRoleActions.request(id, onFetchSuccess));
    }
  }, [dispatch, id, onFetchSuccess]);

  const getListModules = useCallback(() => {
    dispatch(ListModulesActions.request({ all: true }));
  }, [dispatch]);

  const getDatatableColumnConfigs = useCallback(() => {
    dispatch(ListDatatableColumnConfigsActions.request());
  }, [dispatch]);

  const getDatatableFilterConfigs = useCallback(() => {
    dispatch(ListDatatableFilterConfigsActions.request());
  }, [dispatch]);

  const getReportColumnConfigs = useCallback(() => {
    dispatch(ListReportColumnConfigsActions.request());
  }, [dispatch]);

  const onHandleChangeModule = useCallback((data) => {
    setSelectedModule(String(data.value));
  }, []);

  // const onHandleBuildDatatableColumnConfigs = useCallback(() => {
  //   const listConfigs: GroupedPermission = {};

  //   dataDatatableColumnConfigs?.forEach((item) => {
  //     if (!listConfigs.hasOwnProperty(`${item.domain}`)) {
  //       Object.assign(listConfigs, { [`${item.domain}`]: [] });
  //     }

  //     listConfigs[`${item.domain}`].push({
  //       id: `column-${item?.id}`,
  //       label: item?.name,
  //       value: `${item?.id}`,
  //       module_id: `${item.module?.id}` || '',
  //       description: item.description,
  //     });
  //   });

  //   setDatatableColumnConfigs(() => listConfigs);
  // }, [dataDatatableColumnConfigs]);

  // const onHandleBuildDatatableFilterConfigs = useCallback(() => {
  //   const listConfigs: GroupedPermission = {};

  //   dataDatatableFilterConfigs?.forEach((item) => {
  //     if (!listConfigs.hasOwnProperty(`${item.domain}`)) {
  //       Object.assign(listConfigs, { [`${item.domain}`]: [] });
  //     }

  //     listConfigs[`${item.domain}`].push({
  //       id: `filter-${item?.id}`,
  //       label: item?.name,
  //       value: `${item?.id}`,
  //       module_id: `${item.module?.id}` || '',
  //       description: item.description,
  //     });
  //   });

  //   setDatatableFilterConfigs(() => listConfigs);
  // }, [dataDatatableFilterConfigs]);

  // const onHandleBuildReportColumnConfigs = useCallback(() => {
  //   const listConfigs: GroupedPermission = {};

  //   dataReportColumnConfigs?.forEach((item) => {
  //     if (!listConfigs.hasOwnProperty(`${item.domain}`)) {
  //       Object.assign(listConfigs, { [`${item.domain}`]: [] });
  //     }

  //     listConfigs[`${item.domain}`].push({
  //       id: `report-${item?.id}`,
  //       label: item?.name,
  //       value: `${item?.id}`,
  //       module_id: `${item.module?.id}` || '',
  //       description: item.description,
  //     });
  //   });

  //   setReportColumnConfigs(() => listConfigs);
  // }, [dataReportColumnConfigs]);

  useEffect(() => getListModules(), [getListModules]);
  useEffect(() => getDatatableColumnConfigs(), [getDatatableColumnConfigs]);
  useEffect(() => getDatatableFilterConfigs(), [getDatatableFilterConfigs]);
  useEffect(() => getReportColumnConfigs(), [getReportColumnConfigs]);
  useEffect(() => fetchData(), [fetchData]);

  useEffect(() => {
    if (dataDatatableColumnConfigs) {
      setDatatableColumnConfigs(
        buildGroupedPermission(dataDatatableColumnConfigs, 'column')
      );
    }
  }, [dataDatatableColumnConfigs, buildGroupedPermission]);

  useEffect(() => {
    if (dataDatatableFilterConfigs) {
      setDatatableFilterConfigs(
        buildGroupedPermission(dataDatatableFilterConfigs, 'filter')
      );
    }
  }, [dataDatatableFilterConfigs, buildGroupedPermission]);

  useEffect(() => {
    if (dataReportColumnConfigs) {
      setReportColumnConfigs(
        buildGroupedPermission(dataReportColumnConfigs, 'report')
      );
    }
  }, [dataReportColumnConfigs, buildGroupedPermission]);

  return (
    <MainContainer>
      <S.PageHeader>
        <h1>
          <S.IconSetting />
          {getTranslation('configuracoes')}
          <span>{getTranslation('editarTipoUsuario')}</span>
        </h1>
        <S.HeaderButtons>
          <S.ButtonMini btStyle="dark" onClick={() => history.goBack()}>
            <S.IconArrowLeft />
            {getTranslation('voltar')}
          </S.ButtonMini>
        </S.HeaderButtons>
      </S.PageHeader>
      <S.PageContent>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <S.BoxContainer>
            <S.FormRow>
              <Input name="name" label={getTranslation('nome')} />
            </S.FormRow>
            <S.Title>{getTranslation('configuracoesTipo')}</S.Title>
            <S.FormRow>
              <Select
                name="general_module_id"
                label={getTranslation('modulo')}
                isLoading={loadingModules}
                isDisabled={loadingModules}
                options={
                  dataModules.filter((item: any) => Number(item.value) === 1)
                }
                onChange={onHandleChangeModule}
              />
            </S.FormRow>
          </S.BoxContainer>

          <S.CheckContainer>
            <ConfigChecklist
              configs={datatableColumnConfigs}
              defaultValues={defaultDatatableColumnConfig}
              selectedModule={selectedModule}
              namePrefix="datatableConfigs"
              label={getTranslation('configuracoesTabela')}
            />
            <ConfigChecklist
              configs={datatableFilterConfigs}
              defaultValues={defaultDatatableFilterConfigs}
              selectedModule={selectedModule}
              namePrefix="datatableFilters"
              label={getTranslation('configuracoesFiltro')}
            />
            <ConfigChecklist
              configs={reportColumnConfigs}
              defaultValues={defaultReportColumnConfigs}
              selectedModule={selectedModule}
              namePrefix="reportConfigs"
              label={getTranslation('configuracoesRelatorio')}
            />
          </S.CheckContainer>

          <S.FormFooter>
            <S.FormRow>
              <S.Button
                btStyle="cancel"
                type="button"
                onClick={() => history.goBack()}
              >
                {getTranslation('cancelar')}
              </S.Button>
              <S.Button type="submit">
                {loading || loadingUpdateRole ? (
                  <S.Loading />
                ) : (
                    getTranslation('salvar')
                  )}
              </S.Button>
            </S.FormRow>
          </S.FormFooter>
        </Form>
      </S.PageContent>
    </MainContainer>
  );
};
